import { ChangeEvent, useState, useMemo, useEffect } from "react";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { FormattedWorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import { WorkflowSelectFormGroup } from "../../../common/select-form-groups/workflow-select-form-group";
import { CommonWorkflowConfigurationSection } from "../common-workflow-configuration-section/common-workflow-configuration-section";
import { OrganizationSelectFormGroup, RelyingPartySelectFormGroup } from "../../../common/select-form-groups";
import defaultStyles from "../../../common/modals/default-modal/default-modal.less";

type CopyWorkflowSectionProps = {
    labelWidth: LabelWidthTypes;
    canAddNewRelyingParty: boolean;
    canAddRelyingPartyDueToLimits: boolean;
    requestInProgress: boolean;
    isLoadingRequiredData: boolean;
    axnCompanies: Array<AxnCompany>;
    relyingParties: Array<AxnRelyingParty>;
    availableRelyingParties: Array<AxnRelyingParty>;
    workflowConfigurations: Array<FormattedWorkflowConfiguration>;
    workflowName: string;
    selectedWorkflowOrganizationOption: SelectOption;
    selectedWorkflowRelyingPartyOption: SelectOption;
    selectedWorkflowConfigurationOption: SelectOption;
    workflowRedirectUrls: Array<string>;
    handleUpdateVerificationWorkflow: (newOption: SelectOption) => void;
    onAddRelyingPartySuccess: (newAxnRelyingParty: AxnRelyingParty) => void;
    handleUpdateWorkflowName: (event: ChangeEvent<HTMLInputElement>) => void;
    handleUpdateWorkflowOrganization: (newOption: SelectOption) => void;
    handleUpdateWorkflowRelyingParty: (newOption: SelectOption) => void;
    handleUpdateWorkflowRedirectUrl: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
    onClickAddRedirectUrl: () => void;
    removeWorkflowRedirectUrl: (index: number) => void;
};

type CopyWorkflowSectionState = {
    selectedCopyFromOrganizationOption: SelectOption;
    selectedCopyFromRelyingPartyOption: SelectOption;
    availableRelyingParties: Array<AxnRelyingParty>;
};

export const CopyWorkflowSection = (props: CopyWorkflowSectionProps) => {
    const {
        labelWidth,
        canAddNewRelyingParty,
        canAddRelyingPartyDueToLimits,
        requestInProgress,
        isLoadingRequiredData,
        axnCompanies,
        relyingParties,
        availableRelyingParties,
        workflowConfigurations,
        workflowName,
        selectedWorkflowOrganizationOption,
        selectedWorkflowRelyingPartyOption,
        selectedWorkflowConfigurationOption,
        workflowRedirectUrls,
        handleUpdateVerificationWorkflow,
        onAddRelyingPartySuccess,
        handleUpdateWorkflowName,
        handleUpdateWorkflowOrganization,
        handleUpdateWorkflowRelyingParty,
        handleUpdateWorkflowRedirectUrl,
        onClickAddRedirectUrl,
        removeWorkflowRedirectUrl
    } = props;

    const [state, setState] = useState<CopyWorkflowSectionState>({
        selectedCopyFromOrganizationOption: axnCompanies.length === 1 ? mapEntityWithNameAndIdToSelectOption(axnCompanies[0]) : null,
        selectedCopyFromRelyingPartyOption: null,
        availableRelyingParties: relyingParties
    });

    const handleUpdateCopyFromOrganization = (newValue: SelectOption) => {
        if (newValue) {
            const availableRelyingParties = relyingParties.filter((rp: any) => rp.companyID === newValue.value);
            if (state.selectedCopyFromRelyingPartyOption && availableRelyingParties.some(rp => rp.id === state.selectedCopyFromRelyingPartyOption.value)) {
                setState({
                    ...state,
                    selectedCopyFromOrganizationOption: newValue,
                    availableRelyingParties: availableRelyingParties
                })
            } else {
                handleUpdateVerificationWorkflow(null);
                setState({
                    ...state,
                    selectedCopyFromOrganizationOption: newValue,
                    availableRelyingParties: availableRelyingParties,
                    selectedCopyFromRelyingPartyOption: null
                })
            }
        } else {
            setState({
                ...state,
                selectedCopyFromOrganizationOption: null,
                availableRelyingParties: relyingParties
            });
        }
    };

    const handleUpdateCopyFromRelyingParty = (newValue: SelectOption) => {
        if (newValue) {
            let selectedWorkflowOrganizationOption: SelectOption = null;
            let availableRelyingParties = relyingParties;
            const relyingParty = relyingParties.find(rp => rp.id === newValue.value);
            if (relyingParty) {
                const company = axnCompanies.find(company => company.id === relyingParty.companyID);
                if (company) {
                    availableRelyingParties = relyingParties.filter((rp: any) => rp.companyID === company.id);
                    selectedWorkflowOrganizationOption = mapEntityWithNameAndIdToSelectOption(company);
                }
            }

            const availableWorkflowConfigurations = workflowConfigurations.filter(wc => wc.relyingParty?.id === newValue.value);
            if (selectedWorkflowConfigurationOption && !availableWorkflowConfigurations.some(wc => wc.id === selectedWorkflowConfigurationOption?.value)) {
                handleUpdateVerificationWorkflow(null);
            }

            setState({
                ...state,
                availableRelyingParties: availableRelyingParties,
                selectedCopyFromRelyingPartyOption: newValue,
                selectedCopyFromOrganizationOption: selectedWorkflowOrganizationOption
            });
        } else {
            setState({
                ...state,
                selectedCopyFromRelyingPartyOption: null,
            });
        }
    };

    useEffect(() => {
        if (selectedWorkflowConfigurationOption) {
            const selectedWorkflow = workflowConfigurations.find(wc => wc.id === selectedWorkflowConfigurationOption.value);
            if (selectedWorkflow) {
                const company = axnCompanies.find(company => company.id === selectedWorkflow.company?.id)
                const relyingParty = relyingParties.find(rp => rp.id === selectedWorkflow.relyingParty?.id);
                if (company && relyingParty) {
                    setState({
                        ...state,
                        selectedCopyFromOrganizationOption: mapEntityWithNameAndIdToSelectOption(company),
                        selectedCopyFromRelyingPartyOption: mapEntityWithNameAndIdToSelectOption(relyingParty)
                    });
                }
            }
        }
    }, [selectedWorkflowConfigurationOption]);

    const availableCopyFromRelyingParties = useMemo(() => {
        if (state.selectedCopyFromOrganizationOption) {
            return relyingParties.filter(rp => rp.companyID === state.selectedCopyFromOrganizationOption.value)
        }
        return relyingParties;
    }, [state.selectedCopyFromOrganizationOption]);

    const availableCopyFromWorkflowConfigurations = useMemo(() => {
        if (state.selectedCopyFromRelyingPartyOption) {
            return workflowConfigurations.filter(wc => wc.relyingParty.id === state.selectedCopyFromRelyingPartyOption.value);
        } else if (state.selectedCopyFromOrganizationOption) {
            return workflowConfigurations.filter(wc => wc.company.id === state.selectedCopyFromOrganizationOption.value);
        }
        return workflowConfigurations;
    }, [state.selectedCopyFromOrganizationOption, state.selectedCopyFromRelyingPartyOption]);

    return (
        <div>
            <hr />
            <span className={defaultStyles.sectionHeader}>
                <b>{resources.modals.addVerificationWorkflow.copyFrom}</b>
            </span>
            <OrganizationSelectFormGroup
                idPrefix="workflow-copy-from"
                onChange={handleUpdateCopyFromOrganization}
                axnCompanies={axnCompanies}
                selectedOption={state.selectedCopyFromOrganizationOption}
                disabled={requestInProgress || axnCompanies.length === 1}
                isRequired={true}
                isSearchable={true}
                isClearable={axnCompanies.length > 1}
                labelWidth={labelWidth}
            />
            <RelyingPartySelectFormGroup
                idPrefix="workflow-copy-from"
                onChange={handleUpdateCopyFromRelyingParty}
                axnRelyingParties={availableCopyFromRelyingParties}
                selectedOption={state.selectedCopyFromRelyingPartyOption}
                disabled={requestInProgress}
                isRequired={true}
                isSearchable={true}
                isClearable={true}
                labelWidth={labelWidth}
            />
            <WorkflowSelectFormGroup
                idPrefix={"verification-workflow"}
                onChange={handleUpdateVerificationWorkflow}
                workflowConfigurations={availableCopyFromWorkflowConfigurations}
                selectedOption={selectedWorkflowConfigurationOption}
                disabled={requestInProgress || isLoadingRequiredData}
                isLoading={isLoadingRequiredData}
                isRequired={true}
                isSearchable={true}
                isClearable={false}
                labelWidth={labelWidth}
            />
            <hr />
            <span className={defaultStyles.sectionHeader}>
                <b>{resources.modals.addVerificationWorkflow.copyTo}</b>
            </span>
            <CommonWorkflowConfigurationSection
                axnCompanies={axnCompanies}
                canAddNewRelyingParty={canAddNewRelyingParty}
                canAddRelyingPartyDueToLimits={canAddRelyingPartyDueToLimits}
                availableRelyingParties={availableRelyingParties}
                workflowName={workflowName}
                selectedWorkflowOrganizationOption={selectedWorkflowOrganizationOption}
                selectedWorkflowRelyingPartyOption={selectedWorkflowRelyingPartyOption}
                workflowRedirectUrls={workflowRedirectUrls}
                onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                handleUpdateWorkflowName={handleUpdateWorkflowName}
                handleUpdateWorkflowOrganization={handleUpdateWorkflowOrganization}
                handleUpdateWorkflowRelyingParty={handleUpdateWorkflowRelyingParty}
                handleUpdateWorkflowRedirectUrl={handleUpdateWorkflowRedirectUrl}
                onClickAddRedirectUrl={onClickAddRedirectUrl}
                removeWorkflowRedirectUrl={removeWorkflowRedirectUrl}
                labelWidth={labelWidth}
                requestInProgress={requestInProgress}
            />
        </div>
    );
};